// WS-72 step 5: a map that rests (engine-generic; resortMap.rest in a page). VIS-206: on for every map; a check that
// holds and compares frames calls resortMap.rest.keepAwake(true): every frame drawn (the old loop).
// A picture that does not change is not drawn again. Once nobody has touched the map for `after` s and nothing is
// gliding (main.js moving()), the loop reads the picture back twice, `check` s apart; if the two are the same pixel for
// pixel and nothing that travels is in view, the map sleeps: the world keeps ticking (`tick` s steps: herds, rides,
// people walk on where nobody sees them) but nothing is drawn, except one probe picture every `probe` s that is compared
// with the resting one (anything the rules below miss: a late model, the real-time sun, the aurora's own cycle).
// It wakes at once (the next frame is drawn as before, so there is no jump) on any input or programmatic wake
// (main.js: idle.reset, runtime changes, pointerleave), when moving() turns true, when the camera's pose was changed by
// code (checked every frame: setView, a QA harness, the editor), when a probe differs, or when a
// travelling thing is in view: a visible mesh, points or line whose matrix, instances or vertex data changed since the
// last look (looked at every tick while asleep) and whose bounds meet the camera's view.
// Frames are identical to the old loop: it only leaves out pictures that would have been the same.
// The trees do not sway (owner, 2026-10-27: too heavy to process; it kept every forest view drawing), so a still view
// with nothing moving in it can rest. The other saving is main.js's off-screen pause (VIS-206).
// WS-75: pictures are read back from the scene pass's target (`target()`, render.js sceneTarget: the HalfFloat picture
// before the finishing passes, which change only with the camera, the selection and the picture settings: all wake it)
// with readRenderTargetPixelsAsync; a comparison lands a frame or two later and is dropped if the map woke meanwhile.
// WS-78 lane C: what is read back is a digest of that picture, not the picture: one pass hashes each 8x8 block of it bit
// for bit (FNV-1a over the texels' float bits, 4 lanes) into a 1/64-size target. Two pictures give the same digest only
// if they are the same pixel for pixel (a change of one bit in one texel changes its block; a miss is ~2^-92), so the
// decision is the one the full picture gave, for 1/64 of the readback (retina: 0.7 MB instead of 23 MB) and of the compare.
import * as THREE from 'three/webgpu';
import { Fn, Loop, texture, uniform, ivec2, uvec4, uint, int, min, floor, screenCoordinate, floatBitsToUint, uintBitsToFloat } from 'three/tsl';
import { deviceLost } from './gpu/internals.js'; // VIS-267: no readback on a lost device
import { drewAll } from './gpu/pipelines.js'; // VIS-267: a digest three did not draw (read below)

const DS = 8; // digest block (texels per side)
function createDigest(renderer) {
  const src = texture(new THREE.Texture()), size = uniform(new THREE.Vector2(1, 1));
  const mat = new THREE.NodeMaterial(); mat.name = 'rest digest';
  mat.fragmentNode = Fn(() => {
    const base = ivec2(floor(screenCoordinate.xy)).mul(DS).toConst(), last = ivec2(size).sub(1).toConst();
    const h = uvec4(uint(2166136261)).toVar();
    Loop({ start: int(0), end: int(DS * DS), type: 'int', condition: '<' }, ({ i }) => {
      const p = min(base.add(ivec2(i.mod(DS), i.div(DS))), last);
      h.assign(h.bitXor(floatBitsToUint(src.load(p))).mul(uint(16777619)));
    });
    return uintBitsToFloat(h.bitAnd(uint(0x007fffff)).bitOr(uint(0x3f800000))); // 23 hash bits per lane as a float in [1, 2): never NaN or Inf
  })();
  const quad = new THREE.QuadMesh(mat);
  let rt = null;
  return (from) => { // draws the digest of `from` (now, in this task) and returns its target
    const w = Math.ceil(from.width / DS), h = Math.ceil(from.height / DS);
    if (!rt) rt = new THREE.RenderTarget(w, h, { type: THREE.FloatType, depthBuffer: false, minFilter: THREE.NearestFilter, magFilter: THREE.NearestFilter });
    else if (rt.width !== w || rt.height !== h) rt.setSize(w, h);
    src.value = from.texture; size.value.set(from.width, from.height);
    const prev = renderer.getRenderTarget();
    renderer.setRenderTarget(rt);
    try { quad.render(renderer); } finally { renderer.setRenderTarget(prev); }
    return rt;
  };
}

// ---- phone clock (begin): the phone set's one frame clock (main.js PHONE_ON, the rescue set too; computers and lobby
// screens draw as before). An iPhone 15 Pro Max ran hot within 5 minutes: the map drew at the screen's 120 Hz for 12 s
// after every touch, then ~30 a second without end (the trees always sway, so a forest view never rests). On a phone a
// picture is drawn at most `cap` times a second while the camera moves, a finger is down or the `wake` window after
// the last input runs, and `rest` times a second after that for what moves on its own (sway, water, aurora, herds,
// rides, smoke, fire: every one steps on the same drawn passes with the real time since the last, so nothing moves
// slower); it still falls asleep when two pictures match (createRest below). 30 divides 60 and 120 Hz, so every
// picture shows for the same number of refreshes. The shadow keeper's phone numbers are here too (main.js hands them
// to shadows.js createShadowKeep). The only place these numbers live: skill references/performance.md points here.
export const PHONE_PACE = Object.freeze({
  cap: 30, // pictures a second at most while the map moves (a flight, a glide, a finger, the wake window)
  wake: 2.5, // s at the cap after the last input (computers: 12 s at the screen's rate)
  rest: 15, // pictures a second after that, until the map sleeps
  live: 0, // shadow keeper: the movers' live maps redrawn on every picture the phone draws (0; `cap` and `rest` above already bound them). 10 a second (VIS-249) held a snowmobile's shadow 0.65-0.9 m behind it and a sleigh's 12-16 cm, every 2nd-3rd picture a jump (owner, 2026-10-25); a live map is 1024 px at most on a phone, ~4 objects
  liveInView: true, // shadow keeper: the movers' maps are redrawn only while a mover, or the ground its shadow falls on, is in view (a ride behind the camera costs nothing a frame)
  margin: 0.25, // shadow keeper: a still box is redrawn once its centre moved this share of its half-size (computers 0.1)
  boxStep: 0.16, // main.js fitShadow: zooming out the near box grows once the view wants this share more (computers 0.08; zooming in 0.08 everywhere: a box kept wider is coarser, 16 % lost a deck's rail shadows). Any new box size gives the map a new texel grid, and at 1024 px a tree crown's sprays are finer than a texel: their shadows' mottling changes all over the ground, however small the step (10 % at rest: 1-2 levels on average, 30 %: 2-3)
  walkEvery: 4, // shadow keeper: every caster looked at every n-th frame while the view rests (every frame while it moves)
  farMovers: false, // shadow keeper: no far live map (WebGL2, 16 textures): a mover far out casts no far shadow instead of redrawing the far still map every frame
  env: 1, // s between two reflection bakes while the hour moves, at least (computers 0.25; main.js bakeEnv: three's cube + its flat picture)
  envOrder: [5, 0, 5, 4, 5, 1, 5, 3, 5, 2], // the reflections' levels the fade blends, one a frame (main.js stepEnvFade): level 5, the roughest, carries the whole sky fill on a map without render.daylight, so it comes up every other frame (5 in 10: a 3-3-2 rhythm stepped the picture at Lumo dusk)
});
const SLACK = 1 / 240; // a refresh's jitter: at 60 Hz the second callback (33.3 ms) always passes 1/30, at 120 Hz the fourth
/** The phone clock: wait(acc, busy) = true leaves this animation callback out (acc = s since the last drawn pass);
 *  held = a finger (or the mouse button) is down on `target`, or was lifted less than `wake` s ago (the release is an
 *  input too: the glide and the damping run after it); info() for QA (resortMap.rest.info().pace). */
export function createPhoneClock({ target = null, P = PHONE_PACE } = {}) {
  const down = new Set(), st = { passes: 0, waits: 0, at: 'rest' };
  let upAt = -1e9;
  const up = (e) => { if (down.delete(e.pointerId)) upAt = performance.now(); };
  target?.addEventListener('pointerdown', (e) => down.add(e.pointerId), true);
  globalThis.addEventListener?.('pointerup', up, true); globalThis.addEventListener?.('pointercancel', up, true);
  globalThis.addEventListener?.('blur', () => down.clear());
  return {
    ...P,
    get held() { return down.size > 0 || performance.now() - upAt < P.wake * 1000; },
    wait(acc, busy) {
      st.at = busy ? 'cap' : 'rest';
      if (acc < 1 / (busy ? P.cap : P.rest) - SLACK) { st.waits++; return true; }
      st.passes++; return false;
    },
    info: () => ({ ...P, ...st, down: down.size }),
  };
}
// ---- phone clock (end)
/** The reflections fade's schedule (main.js stepEnvFade): each of the picture's 6 levels takes a straight line from what
 *  it shows to the newest bake. `order` null: every level, every frame, in one draw (draw(0, k, 6): started, stepped and
 *  landed together, the six levels always share their numbers, so one blend; VIS-267: one pass, not six). `order`: one
 *  level a frame, its turn in that list (the phone set; a level is one draw and one pass, gpu/envatlas.js). A level
 *  remembers the time since its last step, across a new bake, so a slow device's level still catches up; a level that
 *  has landed draws nothing. */
export function createEnvFade({ order = null } = {}) {
  const left = new Array(6).fill(0), waited = new Array(6).fill(0);
  let turn = 0;
  const blend = (lod, draw, n = 1) => { const k = Math.min(1, waited[lod] / left[lod]); left.fill(k >= 1 ? 0 : left[lod] - waited[lod], lod, lod + n); waited.fill(0, lod, lod + n); draw(lod, k, n); };
  return {
    start(pace) { left.fill(pace); },
    stop() { left.fill(0); waited.fill(0); },
    get busy() { return left.some(Boolean); },
    step(dt, draw) {
      for (let lod = 0; lod < 6; lod++) if (left[lod]) waited[lod] += dt;
      if (!order) { if (left[0]) blend(0, draw, 6); return; }
      for (let n = order.length; n--;) { const lod = order[turn++ % order.length]; if (left[lod]) { blend(lod, draw); return; } }
    },
  };
}

// ---- VIS-267 step 3 life mode (begin): with a far rate (lifepace.js FAR_RATE, the phone set) far small movers are posed on
// one shared tick, so a view where only they move can rest between their poses. While the map draws anyway (awake, or a
// judge with a near mover in view, which fails as before) far movers are posed on every picture (`lifeAwake`: main.js
// hands it to lifePace.frame; owner, 2026-10-26: far rides looked laggy). With only far movers in view the judge never
// holds them (it froze them ~0.5 s): they keep the resting tick and the judge links the stretches between two of their
// poses (`chain`): each pose frame starts a link (its picture the link's reference; the look takes in the poses), each
// frame after it is compared with that reference and with the look (any other change, a near mover = awake again, as a
// failed try), and once the links proven equal add up to `check` s the map sleeps. Readbacks overlap for this (each is
// taken when it is asked: the digest's copy is queued at once), so a link proves its stretch however late its readback
// lands. Asleep, a frame on which the tick posed movers in view is drawn without waking and its
// digest becomes the reference (`life` pictures; while one is still being read, the next quiet frame draws a fresh
// reference first); a near mover in view wakes the map; the probe runs on frames with no pose, so it compares a
// picture whose movers stand where the reference has them, and anything else that changed wakes it as before. Without
// a far rate nothing here runs: the rest is as it was.
// ---- VIS-267 step 3 life mode (end)
/** @param {{ renderer, scene, camera, target: () => THREE.RenderTarget, after?: number, check?: number, probe?: number, tick?: number, life?: object }} o */
export function createRest({ renderer, scene, camera, target, after = 2, check = 0.5, probe = 2, tick = 0.1, life = null }) {
  const F = new THREE.Frustum(), M = new THREE.Matrix4(), S = new THREE.Sphere(), seen = new WeakMap();
  const st = { mode: 'awake', why: 'start', drawn: 0, skipped: 0, probes: 0, wakes: 0, sleeps: 0, life: 0 };
  const now = () => performance.now() / 1000;
  let woke = now(), judgeAt = 0, retry = 0, back = 2, ref = null, nextProbe = 0, lastLook = 0, probing = false;
  let lifeFrame = false, fresh = false, stale = false; // VIS-267 step 3 life mode: lifeFrame = this pass draws their poses; fresh = it draws a new reference; stale = the reference lags a drawn pose
  let chain = false, cur = null, proven = 0, judges = 0; // VIS-267 step 3 life mode: chain = the judge links far poses; cur = its link ({ at, best, ref }); proven = s proven equal; judges = tries so far (a late readback of an old try is dropped)
  const lifeOn = () => !!life && life.rate !== Infinity;
  let gen = 0, reading = 0; // WS-75: gen = wakes so far (a late readback from before a wake is dropped); reading = readbacks in flight (the judge's links overlap them)
  const digest = createDigest(renderer); // WS-78 lane C
  const read = () => { // WS-75: async; resolves with the picture's digest as 32-bit words (null if the map woke meanwhile)
    if (deviceLost(renderer)) return Promise.resolve(null); // VIS-267: a lost device reads nothing back
    let rt;
    // VIS-267: three skips a draw whose program still compiles (warm: dryDraw under the start's cover; on a slow Safari
    // first visit it queues behind every other): its target keeps what it had, zeros at first, and two such reads
    // matched while the picture changed (water and smoke froze asleep). Not drawn = changing; nothing is read
    if (!drewAll(renderer, () => { rt = digest(target()); })) { if (st.mode === 'asleep') api.wake('changing'); else fail('changing'); return Promise.resolve(null); }
    const g = gen;
    reading++;
    return renderer.readRenderTargetPixelsAsync(rt, 0, 0, rt.width, rt.height)
      .then((px) => (g === gen ? new Uint32Array(px.buffer, px.byteOffset, px.byteLength >> 2) : null), () => null)
      .finally(() => { reading--; });
  };
  const same = (a, b) => { if (!a || !b || a.length !== b.length) return false; for (let i = 0; i < a.length; i++) if (a[i] !== b[i]) return false; return true; };
  const verOf = (o) => { // matrix, instances, vertex data: what moves a thing on screen
    const e = o.matrixWorld.elements, g = o.geometry, p = g?.attributes.position;
    return e[12] + e[13] * 3 + e[14] * 7 + e[0] * 11 + e[2] * 13 + e[9] * 17 + (o.isInstancedMesh ? o.instanceMatrix.version * 19 + o.count * 1e4 : 0) + (p ? (p.version ?? p.data.version) * 23 + Math.min(g.drawRange.count, 1e7) : 0) + (o.userData.poseVersion?.() ?? 0) * 29; // VIS-267 step 3: + a pose held in a texture (lazy/folk.js: the people's bones)
  };
  const meets = (o) => { // its bounds meet the view (an instanced mesh: per instance, or the movers' own spheres)
    const g = o.geometry;
    if (o.userData.shadowBounds) { let hit = false; o.userData.shadowBounds((s) => { hit ||= F.intersectsSphere(s); }); return hit; } // (a herd's parts: inside the herd's spheres)
    const p = g.attributes.position;
    if (o.isPoints && p.count <= 8192) { // particles (smoke, steam, sparks): one source's puffs may be far from another's
      const r = Math.max(4, o.material.size || 0); // (a shader's puffs are drawn a few metres across)
      for (let i = 0; i < Math.min(p.count, g.drawRange.count); i++) if (F.intersectsSphere(S.set(S.center.fromBufferAttribute(p, i).applyMatrix4(o.matrixWorld), r))) return true;
      return false;
    }
    if (o.isPoints || o.isLine) g.computeBoundingSphere(); else if (!g.boundingSphere) g.computeBoundingSphere();
    if (o.isInstancedMesh && o.count <= 256) {
      for (let i = 0; i < o.count; i++) { o.getMatrixAt(i, M); if (F.intersectsSphere(S.copy(g.boundingSphere).applyMatrix4(M).applyMatrix4(o.matrixWorld))) return true; }
      return false;
    }
    if (o.isInstancedMesh) { if (!o.boundingSphere) o.computeBoundingSphere(); return F.intersectsSphere(S.copy(o.boundingSphere).applyMatrix4(o.matrixWorld)); }
    return F.intersectsSphere(S.copy(g.boundingSphere).applyMatrix4(o.matrixWorld));
  };
  // WS-75: a thing the graphics chip moves (userData.travels: life.js smoke, a compute pass plus the vertex stage) never
  // changes on the CPU, so it counts as moving whenever its sources (userData.sources: [x, y, z, strength?]; the puffs
  // rise ~9 m and drift ~2 m from them) or, without sources, its bounds are in view
  function travelsInView(o) {
    const src = o.userData.sources;
    if (Array.isArray(src)) return src.some(([x, y, z, k = 1]) => F.intersectsSphere(S.set(S.center.set(x, y + 5 * k, z).applyMatrix4(o.matrixWorld), 8 * Math.max(1, k))));
    return o.geometry && o.frustumCulled !== false ? meets(o) : true; // VIS-267 step 3: one drawn whatever the view (the aurora's bands, sky-anchored) is in view whenever it is shown
  }
  /** the first travelling thing in view since the last look (names it in info().why), or null */
  function travelling() {
    scene.updateMatrixWorld(); camera.updateMatrixWorld();
    F.setFromProjectionMatrix(M.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse));
    let hit = null;
    scene.traverseVisible((o) => {
      if (o.userData.travels) { if (!hit && travelsInView(o)) hit = o; return; } // WS-75: moved on the GPU, always moving
      if (!(o.isMesh || o.isPoints || o.isLine) || !o.geometry) return;
      const v = verOf(o), was = seen.get(o);
      seen.set(o, v);
      if (!hit && was !== undefined && was !== v && meets(o)) hit = o;
    });
    lastLook = now();
    return hit;
  }
  // VIS-271 B7: the camera's pose as 12 numbers in kept arrays (was a string built every pass while asleep)
  const atPose = new Float64Array(12), nowPose = new Float64Array(12);
  const pose = (out) => { const c = camera, p = c.position, q = c.quaternion, v = c.view, on = !!v?.enabled; out[0] = p.x; out[1] = p.y; out[2] = p.z; out[3] = q.x; out[4] = q.y; out[5] = q.z; out[6] = q.w; out[7] = c.fov; out[8] = c.aspect; out[9] = c.zoom; out[10] = on ? v.offsetX : NaN; out[11] = on ? v.offsetY : NaN; return out; };
  const posed = () => { pose(nowPose); for (let i = 0; i < 12; i++) if (nowPose[i] !== atPose[i] && !(nowPose[i] !== nowPose[i] && atPose[i] !== atPose[i])) return false; return true; }; // NaN = no view offset on both
  function sleep(t) {
    st.mode = 'asleep'; st.sleeps++; nextProbe = t + probe; back = 2; pose(atPose); chain = false; stale = false;
  }
  const fail = (why) => { st.mode = 'awake'; st.why = why; retry = now() + back; back = Math.min(back * 2, 16); judgeAt = 0; chain = false; }; // a failed try: awake, the next after 2, 4, 8, then 16 s
  let proofAt = 0; // VIS-267: the last time a link proved anything (a judge never hangs)
  function judgeChain(t) { // VIS-267 step 3 life mode: one pass of a judge that links far poses (the life mode comment above)
    const c = life.counts, j = judges;
    if (c.near > 0) return fail('near mover');
    if (t - proofAt > 2 * check) return fail('changing'); // nothing proved for 1 s (a pose on every pass, or each link one quiet picture that never matched its pose)
    if (c.posed > 0) { travelling(); cur = { at: t, best: 0, pose: read(), ref: null }; return; } // a far pose: a new link from this picture
    const hit = travelling();
    if (hit) return fail(`mover ${hit.name || hit.type}`);
    const L = cur;
    const credit = (b) => {
      proofAt = t;
      if (t - L.at > L.best) { proven += t - L.at - L.best; L.best = t - L.at; }
      if (proven < check) return;
      ref = b; sleep(now());
      if (L !== cur) stale = true; // a far pose drawn since: the next quiet pass draws the reference
    };
    if (!L.ref) { // the link's first quiet picture: a match with the pose's own proves; a difference does not fail (WebGL2/ANGLE: a pose's own picture can come out a bit off), the quiet ones after it decide
      L.ref = read();
      if (L.pose) Promise.all([L.pose, L.ref]).then(([a, b]) => { if (a && b && st.mode === 'judge' && j === judges && same(a, b)) credit(b); });
      return;
    }
    Promise.all([L.ref, read()]).then(([a, b]) => {
      if (!a || !b || st.mode !== 'judge' || j !== judges) return;
      if (!same(a, b)) return fail('changing');
      credit(b);
    });
  }
  let awake = false; // VIS-206: keepAwake (QA)
  const api = {
    /** VIS-206: true = never rest (QA frame checks: the old loop); false = rest again */
    keepAwake(on = true) { awake = !!on; api.wake('keep-awake'); },
    /** programmatic and input wake: draw again from the next frame */
    wake(why = 'input') {
      woke = now(); gen++;
      if (st.mode !== 'awake') { st.wakes++; st.why = why; }
      st.mode = 'awake'; ref = null; judgeAt = 0; back = 2; retry = 0; chain = false; stale = false;
      api.nap?.wake(); // VIS-267: the loop asks for the browser's next frame at once (createNap)
    },
    /** VIS-267: the digest drawn once, dry under the start's cover (main.js startStep): its program compiles there,
     *  not at the first rest check */
    warm: () => digest(target()),
    get asleep() { return st.mode === 'asleep'; },
    /** VIS-267 round 2 C (lazy/sharp.js): asleep, the picture changes on purpose (drawn sharp): the next pass draws a
     *  fresh reference, the probes compare with that one */
    refresh() { if (st.mode === 'asleep') stale = true; },
    /** VIS-267 step 3: this pass is drawn anyway (awake, or a judge not linking far poses): far movers are posed on it
     *  too (main.js hands it to lifePace.frame); asleep or linking they keep the far tick */
    get lifeAwake() { return awake || st.mode === 'awake' || (st.mode === 'judge' && !chain); },
    /** VIS-267 step 3: this pass draws a picture that must show nothing new (a probe, a fresh reference, the judge's
     *  two pictures): what only moves while the map draws anyway (smoke and steam) waits */
    get still() { return probing || fresh || st.mode === 'judge'; },
    /** the map sleeps or judges whether it can (asleep: its probes and fresh references; the judge's pictures), read
     *  before the world steps (main.js: the phone set's clouds hold still then, so those pictures can match) */
    get resting() { return !awake && st.mode !== 'awake'; },
    /** per loop pass (before the frame is stepped): true = skip this pass entirely (asleep, under the tick) */
    idle(moving, acc) {
      if (awake) return false;
      if (moving) { if (st.mode !== 'awake') api.wake('moving'); woke = now(); return false; }
      if (st.mode === 'asleep' && !posed()) { api.wake('camera'); return false; } // a camera moved by code (setView, a harness, the editor)
      return st.mode === 'asleep' && acc < tick;
    },
    /** before the draw: false = draw nothing this pass */
    draw() {
      const t = now();
      probing = false; lifeFrame = false; fresh = false;
      if (awake) return true;
      if (st.mode === 'awake' || st.mode === 'judge') return true;
      if (lifeOn()) { // VIS-267 step 3 life mode
        const c = life.counts;
        if (c.near > 0) { api.wake('near mover'); return true; }
        if (c.posed > 0) { lifeFrame = true; st.life++; travelling(); return true; } // (the look takes in the movers' poses)
      }
      if (stale && !reading) { fresh = true; return true; } // (life mode, or refresh())
      if (t - lastLook >= tick && travelling()) { api.wake('mover'); return true; }
      if (t >= nextProbe && !stale && !reading) { probing = true; return true; }
      st.skipped++;
      return false;
    },
    /** after the draw */
    drawn() {
      const t = now();
      st.drawn++;
      if (awake) return;
      if (probing) {
        st.probes++; nextProbe = t + probe;
        read().then((px) => { if (px && !same(px, ref)) api.wake('probe'); });
        return;
      }
      if (lifeFrame || fresh) { // VIS-267 step 3 life mode: this picture is the new reference
        if (reading) { stale = true; return; }
        stale = false;
        read().then((px) => { if (px && st.mode === 'asleep') ref = px; });
        return;
      }
      if (st.mode === 'judge' && chain) return judgeChain(t); // VIS-267 step 3 life mode (its readbacks overlap)
      if (reading) return; // WS-75: one readback at a time
      if (st.mode === 'awake') {
        if (t - woke < after || t < retry) return;
        travelling(); // a first look: from here on a change counts
        st.mode = 'judge'; ref = null; judgeAt = Infinity; judges++;
        chain = lifeOn() && life.counts.near === 0; // VIS-267 step 3: only far movers in view: the judge links their poses (no hold)
        if (chain) { proven = 0; proofAt = t; cur = { at: t, best: 0, pose: read(), ref: null }; return; }
        read().then((px) => { if (px && st.mode === 'judge') { ref = px; judgeAt = now() + check; } });
      } else if (st.mode === 'judge' && t >= judgeAt) {
        const mover = travelling();
        judgeAt = Infinity;
        read().then((px) => {
          if (!px || st.mode !== 'judge') return;
          const at = now();
          if (!mover && same(px, ref)) { ref = px; sleep(at); return; }
          fail(mover ? `mover ${mover.name || mover.type}` : 'changing');
        });
      }
    },
    info: () => ({ ...st, keepAwake: awake, hidden: api.hidden(), pace: api.pace?.info() ?? null }),
    /** VIS-206: is the map paused, off screen or in a hidden tab? (main.js sets the real one) */
    hidden: () => false,
    /** the phone clock (createPhoneClock) when the phone set is on (main.js sets it), else null: info().pace */
    pace: null,
    // (nap: the loop's frame gate, createNap below, when main.js sets one: wake() hands it on)
  };
  return api;
}
// ---- VIS-267 resting loop (begin): asleep, the map asked for every refresh (~120 a second on a 120 Hz phone) and most
// callbacks returned at once. Now three's loop asks for its next frame through this (its context, main.js): while
// parked() the callback is kept and run from a timer every 34 ms with the time then; parked() === 2 (paused off
// screen): no timer. wake() hands it to the browser's next frame at once (rest.wake calls it: every input, runtime
// change, probe, mover, camera or moving() wake; the off-screen pause's return), never back in time. 34 ms: each check
// a pass makes (the phone clock's 15 a second, the computers' 31, moving(), the camera) comes when it came before, the
// world steps at 10 Hz as before, and a picture is drawn only when a step asks for one (a probe, a far pose).
export function createNap(parked) {
  let f = null, at = 0;
  const run = (g) => { if (f === g) { f = null; g(at = performance.now()); } };
  return {
    requestAnimationFrame(g) { const p = parked(); if (!p) return requestAnimationFrame(g); f = g; if (p < 2) setTimeout(run, 34, g); return 0; },
    cancelAnimationFrame(i) { f = null; cancelAnimationFrame(i); },
    wake() { const g = f; f = null; if (g) requestAnimationFrame((t) => g(Math.max(t, at))); },
  };
}
// ---- VIS-267 resting loop (end)
